import { useState } from 'react';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { identity } from '@repletoryreach/sdk-api';
import { Stack, Card, Title, Text, Group, Button, Badge } from '@repletoryreach/sdk-ui';
import { Alert } from '@repletoryreach/sdk-ui/data-display';
import { notifySuccess, notifyError } from '@repletoryreach/sdk-ui/notifications';
import { useT } from '../../../i18n/I18nProvider';
import { formatRelative } from '../../../lib/format';
import { PushUnavailableError, disablePush, enablePush, pushSupport, storedPushToken } from '../../../lib/push';

const DEVICES_KEY = ['push', 'devices'];

/**
 * Notifications on this device (owner, 9 October 2026): one switch for this browser, and the person's other
 * devices with Remove. Which alerts arrive is chosen below, per alert, with the Push tick.
 */
export function PushDeviceCard() {
  const t = useT();
  const qc = useQueryClient();
  const config = useQuery({ queryKey: ['push', 'config'], queryFn: () => identity.getPushConfig(), staleTime: 300_000 });
  const devices = useQuery({ queryKey: DEVICES_KEY, queryFn: () => identity.listPushDevices() });
  const [support, setSupport] = useState(pushSupport());
  const [onHere, setOnHere] = useState(Boolean(storedPushToken()) && support === 'supported');

  const turnOn = useMutation({
    mutationFn: () => enablePush(),
    onSuccess: () => {
      setOnHere(true);
      void qc.invalidateQueries({ queryKey: DEVICES_KEY });
      notifySuccess(t('settings.notifications.push.onToast'), t('settings.notifications.push.title'));
    },
    onError: (e) => {
      setSupport(pushSupport());
      const reason = e instanceof PushUnavailableError ? e.reason : null;
      notifyError(reason ? t(`settings.notifications.push.error.${reason}`) : (e instanceof Error ? e.message : t('common.unknownError')),
        t('settings.notifications.push.title'));
    }
  });
  const turnOff = useMutation({
    mutationFn: () => disablePush(),
    onSuccess: () => {
      setOnHere(false);
      void qc.invalidateQueries({ queryKey: DEVICES_KEY });
      notifySuccess(t('settings.notifications.push.offToast'), t('settings.notifications.push.title'));
    }
  });
  const remove = useMutation({
    mutationFn: (id: string) => identity.removePushDevice(id),
    onSuccess: () => void qc.invalidateQueries({ queryKey: DEVICES_KEY }),
    onError: (e) => notifyError(e instanceof Error ? e.message : t('common.unknownError'), t('settings.notifications.push.title'))
  });

  const enabled = config.data?.data.enabled === true;
  const list = Array.isArray(devices.data?.data) ? devices.data.data : [];

  return (
    <Card withBorder data-guide="notifications-push" data-testid="push-device-card">
      <Stack gap="sm">
        <Group justify="space-between" align="flex-start" wrap="wrap" gap="sm">
          <Stack gap={2} style={{ flex: '1 1 320px' }}>
            <Title order={4}>{t('settings.notifications.push.title')}</Title>
            <Text role="bodySmall" c="var(--rr-color-text-muted)">{t('settings.notifications.push.description')}</Text>
          </Stack>
          {enabled && support === 'supported' && (
            onHere
              ? <Button size="sm" variant="default" loading={turnOff.isPending} onClick={() => turnOff.mutate()} data-testid="push-off">{t('settings.notifications.push.turnOff')}</Button>
              : <Button size="sm" loading={turnOn.isPending} onClick={() => turnOn.mutate()} data-testid="push-on">{t('settings.notifications.push.turnOn')}</Button>
          )}
        </Group>
        {config.data && !enabled && <Alert color="info">{t('settings.notifications.push.notConfigured')}</Alert>}
        {enabled && support === 'unsupported' && <Alert color="info">{t('settings.notifications.push.error.unsupported')}</Alert>}
        {enabled && support === 'blocked' && <Alert color="warning">{t('settings.notifications.push.error.denied')}</Alert>}
        {list.length > 0 && (
          <Stack gap={6} data-testid="push-devices">
            <Text role="caption" fw={650} c="var(--rr-color-text-muted)">{t('settings.notifications.push.yourDevices')}</Text>
            {list.map((d) => (
              <Group key={d.id} justify="space-between" wrap="nowrap">
                <Group gap="xs" wrap="nowrap">
                  <Text role="bodySmall">{d.label || t(`settings.notifications.push.platform.${d.platform}`)}</Text>
                  <Badge size="xs" variant="light">{t(`settings.notifications.push.platform.${d.platform}`)}</Badge>
                  <Text role="caption" c="var(--rr-color-text-muted)">{t('settings.notifications.push.lastSeen', { when: formatRelative(d.last_seen_at) })}</Text>
                </Group>
                <Button size="xs" variant="subtle" color="red" loading={remove.isPending && remove.variables === d.id} onClick={() => remove.mutate(d.id)}>
                  {t('settings.notifications.push.remove')}
                </Button>
              </Group>
            ))}
          </Stack>
        )}
      </Stack>
    </Card>
  );
}
